/**
 * Guest address persistence — the customer's selected service location
 * before they create an account.
 *
 * Address-first booking flow: the customer must pin a location before
 * any service catalog, price, or slot is shown to them. For unauthed
 * (guest) visitors we keep that location in localStorage so a refresh
 * doesn't kick them back to the address picker.
 *
 * When the customer later logs in, the AuthProvider re-checks their
 * saved server-side addresses; if none exist, we POST the guest pin as
 * their first saved address (transparent migration). If server-side
 * addresses do exist, the default is preferred and the guest pin is
 * discarded.
 *
 * Shape mirrors the subset of `accountApi.Address` we can resolve
 * client-side (no `id`, no `createdAt`) plus `zoneNameEn`/`Ar` for the
 * resolved B2C zone (from `POST /b2c/serviceability` via the
 * `servicesByArea` endpoint).
 */

const STORAGE_KEY = 'luvelle_guest_address_v1';

export interface GuestAddress {
  label: string;
  lat: number;
  lng: number;
  city: string | null;
  district: string | null;
  street: string | null;
  building: string | null;
  postalCode: string | null;
  formatted: string | null;
  zone: {
    id: string;
    nameEn: string;
    nameAr: string;
  } | null;
  // Resolved B2C district (from the serviceability check). May differ
  // from the geocoded `district` (OSM string vs Watan reference data).
  resolvedDistrict: {
    id: string;
    nameEn: string;
    nameAr: string;
  } | null;
  /** When the customer saved this address (ISO 8601). Used to invalidate
   *  stale guest addresses after a long period. */
  savedAt: string;
}

export function readGuestAddress(): GuestAddress | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GuestAddress;
    // Defensive: shape check.
    if (typeof parsed.lat !== 'number' || typeof parsed.lng !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeGuestAddress(address: GuestAddress): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(address));
  } catch {
    /* quota or disabled — silently swallow */
  }
}

export function clearGuestAddress(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}

/** Build a fresh `GuestAddress` from raw map + geocode + zone data. */
export function buildGuestAddress(params: {
  label?: string;
  lat: number;
  lng: number;
  geocoded?: {
    city?: string;
    district?: string;
    street?: string;
    building?: string;
    postalCode?: string;
    formatted?: string;
  };
  zone?: { id: string; nameEn: string; nameAr: string } | null;
  resolvedDistrict?: { id: string; nameEn: string; nameAr: string } | null;
}): GuestAddress {
  return {
    label: params.label ?? 'Home',
    lat: params.lat,
    lng: params.lng,
    city: params.geocoded?.city ?? null,
    district: params.geocoded?.district ?? null,
    street: params.geocoded?.street ?? null,
    building: params.geocoded?.building ?? null,
    postalCode: params.geocoded?.postalCode ?? null,
    formatted: params.geocoded?.formatted ?? null,
    zone: params.zone ?? null,
    resolvedDistrict: params.resolvedDistrict ?? null,
    savedAt: new Date().toISOString(),
  };
}
